import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, ChevronDown, ChevronRight, Play, ExternalLink, Loader2, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { toast } from "sonner";

interface DemoRow {
  id: string;
  prospect_name: string;
  demo_date: string;
  demo_time: string | null;
  status: string;
  outcome: string | null;
  closer_id: string | null;
  closer_name?: string;
}

export default function ClientDemosDownloads() {
  const { clientId } = useRole();
  const [demosOpen, setDemosOpen] = useState(true);
  const [demos, setDemos] = useState<DemoRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDemos();
  }, [clientId]);

  const loadDemos = async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    try {
      // Fetch demos for the current client, joined with closer name
      const { data, error } = await supabase
        .from('demos')
        .select('id, prospect_name, demo_date, demo_time, status, outcome, closer_id, closers(name)')
        .eq('client_id', clientId)
        .order('demo_date', { ascending: false });

      if (error) throw error;

      const mapped: DemoRow[] = (data || []).map((d: any) => ({
        id: d.id,
        prospect_name: d.prospect_name,
        demo_date: d.demo_date,
        demo_time: d.demo_time,
        status: d.status,
        outcome: d.outcome,
        closer_id: d.closer_id,
        closer_name: d.closers?.name ?? 'Unassigned',
      }));

      setDemos(mapped);
    } catch (error) {
      console.error('Error loading demos:', error);
      toast.error('Failed to load demos');
    } finally {
      setLoading(false);
    }
  };

  const completedCount = demos.filter(d => d.status === 'completed').length;

  const getStatusVariant = (status: string): "success" | "info" | "warning" | "error" | "default" => {
    switch (status) {
      case 'completed': return 'success';
      case 'scheduled': return 'info';
      case 'no_show': return 'warning';
      case 'cancelled': return 'error';
      case 'rescheduled': return 'default';
      default: return 'default';
    }
  };

  const formatDateTime = (date: string, time: string | null) => {
    const d = new Date(date + 'T' + (time || '00:00:00'));
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      ...(time ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Demos</h1>
        <p className="text-muted-foreground">Track your booked demos</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <VentureCard>
          <VentureCardContent className="text-center">
            <p className="text-3xl font-bold">{demos.length}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-sm text-muted-foreground">Total Demos</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>All demos scheduled for your account</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center">
            <p className="text-3xl font-bold text-green-400">{completedCount}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-sm text-muted-foreground">Completed</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Demos where the prospect attended the meeting</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Demos */}
      <VentureCard>
        <Collapsible open={demosOpen} onOpenChange={setDemosOpen}>
          <CollapsibleTrigger asChild>
            <VentureCardHeader className="cursor-pointer hover:bg-surface transition-colors">
              <div className="flex items-center gap-3">
                {demosOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                <Calendar className="w-5 h-5 text-secondary" />
                <VentureCardTitle>All Demos</VentureCardTitle>
              </div>
            </VentureCardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <VentureCardContent>
              {demos.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="font-medium mb-1">No demos yet</p>
                  <p className="text-sm text-muted-foreground">Your booked demos will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead><tr className="border-b border-border"><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Prospect</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date & Time</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Closer</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th></tr></thead>
                    <tbody>
                      {demos.map(demo => (
                        <tr key={demo.id} className="border-b border-border hover:bg-surface"><td className="py-3 px-4 font-medium">{demo.prospect_name}</td><td className="py-3 px-4 text-muted-foreground">{formatDateTime(demo.demo_date, demo.demo_time)}</td><td className="py-3 px-4">{demo.closer_name}</td><td className="py-3 px-4"><BadgeVenture variant={getStatusVariant(demo.status)}>{demo.status.replace('_', ' ')}</BadgeVenture></td><td className="py-3 px-4"><div className="flex gap-2"><Button variant="ghost" size="sm"><Play className="w-4 h-4" /></Button><Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button></div></td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </VentureCardContent>
          </CollapsibleContent>
        </Collapsible>
      </VentureCard>
    </motion.div>
  );
}
